/**
 * In-app PDF preview support for a mail attachment.
 *
 * The primary render is native — a `blob:` URL of the fetched bytes handed to an `<iframe>`,
 * so the browser's own PDF engine draws it with full fidelity (`AttachmentPreview.tsx` owns
 * the `<iframe>` and the timeout/toggle logic; this module only fetches the bytes and, when
 * the native render doesn't pan out, extracts text from them as the fallback).
 *
 * `unpdf` is already a dependency, already used the same way in
 * `src/lib/api/useMailExtraction.ts`'s `prepareAttachment` (`getDocumentProxy` + `extractText`,
 * lazy-imported so the PDF.js build never enters the main bundle). This module doesn't import
 * that file — it's a separate call site with a separate purpose (an interactive preview, not
 * an AI-extraction payload) — but the extraction call itself is identical on purpose, so the
 * two code paths behave the same way against the same file.
 */

export type PdfFetchFailureReason = 'fetch_failed' | 'empty'

import { downloadAttachmentBlob } from './api/attachmentBlob'

export interface PdfFetchFailure {
  ok: false
  reason: PdfFetchFailureReason
  detail: string
}

export interface PdfFetchSuccess {
  ok: true
  bytes: Uint8Array
  /** An object URL over the same bytes, for the `<iframe>` to point at directly — avoids a
   * second fetch of a (possibly large) file just to hand the browser something to render. The
   * caller owns its lifetime and must `URL.revokeObjectURL` it when done. */
  blobUrl: string
}

export type PdfFetchResult = PdfFetchSuccess | PdfFetchFailure

/** The uncached path, kept as the default so this module needs no query client of its own —
 * `downloadAttachmentBlob` because it carries the proxy bearer the deployed `/api` gate wants. */
const defaultLoadBlob = downloadAttachmentBlob

/** Fetch `url` and return its bytes plus a ready-to-render object URL. Every failure is a
 * value, not a throw — same discipline as `xlsxPreview.ts`'s `fetchAndParseXlsxPreview`.
 *
 * `loadBlob` lets the caller supply a CACHED download (`api/attachmentBlob.ts`) so that
 * previewing a PDF and then reading the mail with AI costs one request rather than two. It
 * defaults to a plain authenticated fetch, so callers with no query client are unaffected. */
export async function fetchPdfBytes(url: string, loadBlob?: (url: string) => Promise<Blob>): Promise<PdfFetchResult> {
  let blob: Blob
  try {
    blob = loadBlob ? await loadBlob(url) : await defaultLoadBlob(url)
  } catch (error) {
    // A non-2xx and an unreachable server are both `fetch_failed` to the reader; the thrown
    // message carries the status when there was one.
    const detail = error instanceof Error && /\d{3}/.test(error.message)
      ? error.message
      : 'Could not reach the file — check your connection.'
    return { ok: false, reason: 'fetch_failed', detail }
  }

  const bytes = new Uint8Array(await blob.arrayBuffer())
  if (bytes.length === 0) {
    return { ok: false, reason: 'empty', detail: 'This file is empty.' }
  }

  const blobUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
  return { ok: true, bytes, blobUrl }
}

/** Extract plain text from PDF bytes already in hand. Never throws — a scanned/image-only PDF
 * or a corrupt file yields `''`, which the caller treats as "text extraction didn't work
 * either" rather than a crash. */
export async function extractPdfText(bytes: Uint8Array): Promise<string> {
  try {
    const { extractText, getDocumentProxy } = await import('unpdf')
    const pdf = await getDocumentProxy(bytes)
    const extracted = await extractText(pdf, { mergePages: true })
    return Array.isArray(extracted.text) ? extracted.text.join('\n') : String(extracted.text ?? '')
  } catch {
    return ''
  }
}
